import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../../hooks/useAuth';

const PRACTICES = [
  'Commitment',
  'Communication',
  'Implementation',
  'Measurement',
  'Accountability and Transparency',
];

const GES_VECTORS = [
  {
    id: 'pay_equity',
    name: 'Pay Equity',
    parameters: [
      {
        id: 'pe_approach',
        label: 'Approach to advance pay equity',
        type: 'management_framework',
      },
      {
        id: 'pe_hourly_ratio',
        label: 'Women/men compensation ratio — hourly',
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 60 },
          { score: 2, min: 61, max: 75 },
          { score: 3, min: 76, max: 85 },
          { score: 4, min: 86, max: 95 },
          { score: 5, min: 96, max: 999 },
        ],
      },
      {
        id: 'pe_salaried_ratio',
        label: 'Women/men compensation ratio — salaried',
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 60 },
          { score: 2, min: 61, max: 75 },
          { score: 3, min: 76, max: 85 },
          { score: 4, min: 86, max: 95 },
          { score: 5, min: 96, max: 999 },
        ],
      },
      {
        id: 'pe_top10_ratio',
        label: 'Women/men ratio — top 10% compensated',
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 60 },
          { score: 2, min: 61, max: 75 },
          { score: 3, min: 76, max: 85 },
          { score: 4, min: 86, max: 95 },
          { score: 5, min: 96, max: 999 },
        ],
      },
    ],
  },
  {
    id: 'womens_workforce_participation',
    name: "Women's Workforce Participation",
    parameters: [
      {
        id: 'wwp_recruitment_approach',
        label: 'Approach to promote gender diversity in recruitment',
        type: 'management_framework',
      },
      {
        id: 'wwp_salaried_pct',
        label: "Women's percent of salaried employees",
        type: 'quantitative_band',
        ushaped: true, // U-SHAPED BAND: do not change to monotonic
        bands: [
          { score: 1, ranges: [{ min: 0, max: 10 }, { min: 91, max: 100 }] },
          { score: 2, ranges: [{ min: 11, max: 20 }, { min: 81, max: 90 }] },
          { score: 3, ranges: [{ min: 21, max: 30 }, { min: 71, max: 80 }] },
          { score: 4, ranges: [{ min: 31, max: 40 }, { min: 61, max: 70 }] },
          { score: 5, ranges: [{ min: 41, max: 60 }] },
        ],
      },
      {
        id: 'wwp_hourly_pct',
        label: "Women's percent of hourly workers",
        type: 'quantitative_band',
        ushaped: true, // U-SHAPED BAND: do not change to monotonic
        bands: [
          { score: 1, ranges: [{ min: 0, max: 10 }, { min: 91, max: 100 }] },
          { score: 2, ranges: [{ min: 11, max: 20 }, { min: 81, max: 90 }] },
          { score: 3, ranges: [{ min: 21, max: 30 }, { min: 71, max: 80 }] },
          { score: 4, ranges: [{ min: 31, max: 40 }, { min: 61, max: 70 }] },
          { score: 5, ranges: [{ min: 41, max: 60 }] },
        ],
      },
      {
        id: 'wwp_turnover_ratio',
        label: "Women's turnover ratio (% of male turnover)",
        type: 'quantitative_band',
        inverted: true, // INVERTED BAND: lower value = higher score
        bands: [
          { score: 1, min: 151, max: 999 },
          { score: 2, min: 131, max: 150 },
          { score: 3, min: 116, max: 130 },
          { score: 4, min: 106, max: 115 },
          { score: 5, min: 0, max: 105 },
        ],
      },
    ],
  },
  {
    id: 'gender_diverse_leadership_governance',
    name: 'Gender-Diverse Leadership & Governance',
    parameters: [
      {
        id: 'gdl_board_representation',
        label: "Women's board representation (%)",
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 0 },
          { score: 2, min: 1, max: 10 },
          { score: 3, min: 11, max: 25 },
          { score: 4, min: 26, max: 45 },
          { score: 5, min: 46, max: 999 },
        ],
      },
      {
        id: 'gdl_senior_mgmt_representation',
        label: "Women's senior management representation (%)",
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 0 },
          { score: 2, min: 1, max: 10 },
          { score: 3, min: 11, max: 25 },
          { score: 4, min: 26, max: 45 },
          { score: 5, min: 46, max: 999 },
        ],
      },
      {
        id: 'gdl_middle_mgmt_representation',
        label: "Women's middle management representation (%)",
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 0 },
          { score: 2, min: 1, max: 10 },
          { score: 3, min: 11, max: 25 },
          { score: 4, min: 26, max: 45 },
          { score: 5, min: 46, max: 999 },
        ],
      },
      {
        id: 'gdl_ownership',
        label: "Women's ownership (% of total ownership)",
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 0 },
          { score: 2, min: 1, max: 10 },
          { score: 3, min: 11, max: 25 },
          { score: 4, min: 26, max: 45 },
          { score: 5, min: 46, max: 999 },
        ],
      },
    ],
  },
  {
    id: 'benefits_professional_development',
    name: 'Benefits & Professional Development',
    parameters: [
      { id: 'bpd_parental_leave', label: 'Approach to paid parental leave', type: 'management_framework' },
      { id: 'bpd_health_insurance', label: 'Health insurance — gender-specific needs', type: 'management_framework' },
      { id: 'bpd_professional_development', label: "Approach to women's professional development", type: 'management_framework' },
      { id: 'bpd_nondiscrimination', label: 'Non-discrimination in performance review', type: 'management_framework' },
    ],
  },
  {
    id: 'safe_healthy_workplace_environment',
    name: 'Safe & Healthy Workplace Environment',
    parameters: [
      { id: 'shw_environmental_impact', label: 'Environmental impact on women and girls', type: 'management_framework' },
      { id: 'shw_sexual_harassment', label: 'Approach to sexual harassment and GBV', type: 'management_framework' },
      { id: 'shw_occupational_health_safety', label: "Occupational health — women's specific needs", type: 'management_framework' },
      { id: 'shw_inclusion_transparency', label: 'Inclusion, transparency and respect policies', type: 'management_framework' },
    ],
  },
  {
    id: 'women_powered_value_chains',
    name: 'Women-Powered Value Chains',
    parameters: [
      { id: 'wpvc_supplier_diversity', label: 'Gender diversity in supplier workforce', type: 'management_framework' },
      { id: 'wpvc_target_women_customers', label: 'Approach to target women customers', type: 'management_framework' },
      {
        id: 'wpvc_procurement_dept',
        label: 'Women in procurement department (%)',
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 0 },
          { score: 2, min: 1, max: 10 },
          { score: 3, min: 11, max: 25 },
          { score: 4, min: 26, max: 45 },
          { score: 5, min: 46, max: 999 },
        ],
      },
      {
        id: 'wpvc_marketing_product_design',
        label: 'Women in marketing and product design (%)',
        type: 'quantitative_band',
        bands: [
          { score: 1, min: 0, max: 0 },
          { score: 2, min: 1, max: 10 },
          { score: 3, min: 11, max: 25 },
          { score: 4, min: 26, max: 45 },
          { score: 5, min: 46, max: 999 },
        ],
      },
    ],
  },
];

function scoreManagementFramework(checkedPractices) {
  return Math.min(5, (checkedPractices || []).length);
}

function scoreQuantitativeBand(value, parameter) {
  const pct = parseFloat(value);
  if (isNaN(pct)) return null;

  if (parameter.ushaped) {
    // U-SHAPED BAND: do not change to monotonic
    for (const band of parameter.bands) {
      for (const range of band.ranges) {
        if (pct >= range.min && pct <= range.max) return band.score;
      }
    }
    return 1;
  }

  // Standard or INVERTED BAND: lower value = higher score — both use same lookup
  for (const band of parameter.bands) {
    if (pct >= band.min && pct <= band.max) return band.score;
  }
  return 1;
}

function calculateVectorScore(parameters, parameterScores) {
  const applicable = parameters.filter(
    (p) => !parameterScores[p.id]?.na && parameterScores[p.id]?.score != null,
  );
  if (applicable.length === 0) return null;
  const sum = applicable.reduce((s, p) => s + parameterScores[p.id].score, 0);
  return Math.round((sum / applicable.length) * 10) / 10;
}

function calculateOverallGES(vectorScores) {
  const applicable = Object.values(vectorScores).filter((s) => s != null);
  if (applicable.length === 0) return null;
  const sum = applicable.reduce((s, v) => s + v, 0);
  return Math.round((sum / applicable.length) * 10) / 10;
}

function scoreColor(score5) {
  if (score5 == null) return 'var(--color-text-secondary)';
  if (score5 >= 3.5) return '#2d7a4f';
  if (score5 >= 2.0) return '#c9952a';
  return '#c0392b';
}

function scoreBg(score5) {
  if (score5 == null) return 'transparent';
  if (score5 >= 3.5) return '#e8f5ee';
  if (score5 >= 2.0) return '#fff8e1';
  return '#fde8e8';
}

function buildInitialState() {
  const state = {};
  GES_VECTORS.forEach((v) => {
    state[v.id] = {};
    v.parameters.forEach((p) => {
      state[v.id][p.id] = { score: null, na: false, practices_present: [], input_pct: '' };
    });
  });
  return state;
}

export default function SEAFGESFramework({ dealId }) {
  const { user, userProfile } = useAuth();
  const [paramState, setParamState] = useState(buildInitialState);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'deals', dealId, 'frameworks', 'seaf_ges'))
      .then((snap) => {
        if (snap.exists() && snap.data().vectors) {
          const saved = snap.data().vectors;
          const state = buildInitialState();
          GES_VECTORS.forEach((v) => {
            if (saved[v.id]?.parameters) {
              v.parameters.forEach((p) => {
                const sp = saved[v.id].parameters[p.id];
                if (sp) {
                  state[v.id][p.id] = {
                    score: sp.score ?? null,
                    na: sp.na ?? false,
                    practices_present: sp.practices_present || [],
                    input_pct: sp.input_pct != null ? String(sp.input_pct) : '',
                  };
                }
              });
            }
          });
          setParamState(state);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealId]);

  function togglePractice(vectorId, paramId, practice) {
    setParamState((prev) => {
      const current = prev[vectorId][paramId].practices_present || [];
      const next = current.includes(practice)
        ? current.filter((p) => p !== practice)
        : [...current, practice];
      const score = scoreManagementFramework(next);
      return {
        ...prev,
        [vectorId]: {
          ...prev[vectorId],
          [paramId]: { ...prev[vectorId][paramId], practices_present: next, score },
        },
      };
    });
  }

  function setInputPct(vectorId, paramId, value, parameter) {
    setParamState((prev) => {
      const score = scoreQuantitativeBand(value, parameter);
      return {
        ...prev,
        [vectorId]: {
          ...prev[vectorId],
          [paramId]: { ...prev[vectorId][paramId], input_pct: value, score },
        },
      };
    });
  }

  function toggleNa(vectorId, paramId) {
    setParamState((prev) => ({
      ...prev,
      [vectorId]: {
        ...prev[vectorId],
        [paramId]: { ...prev[vectorId][paramId], na: !prev[vectorId][paramId].na, score: null },
      },
    }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      const vectorScores = {};
      const vectorsOut = {};
      GES_VECTORS.forEach((v) => {
        vectorScores[v.id] = calculateVectorScore(v.parameters, paramState[v.id]);
        const paramsOut = {};
        v.parameters.forEach((p) => {
          const ps = paramState[v.id][p.id];
          const entry = { score: ps.score, na: ps.na };
          if (p.type === 'management_framework') {
            entry.practices_present = ps.practices_present;
          } else {
            entry.input_pct = ps.input_pct !== '' ? parseFloat(ps.input_pct) : null;
          }
          paramsOut[p.id] = entry;
        });
        vectorsOut[v.id] = {
          parameters: paramsOut,
          vector_score: vectorScores[v.id],
        };
      });

      const overall = calculateOverallGES(vectorScores);
      await setDoc(doc(db, 'deals', dealId, 'frameworks', 'seaf_ges'), {
        completed_by: user?.uid || '',
        completed_by_name: userProfile?.full_name || userProfile?.display_name || '',
        completed_at: new Date().toISOString(),
        timing_note: 'Completed during Due Diligence — not at IC1 stage',
        vectors: vectorsOut,
        overall_score: overall,
        overall_score_10pt: overall != null ? Math.round(overall * 2 * 10) / 10 : null,
      });
      setSaveMsg('Saved successfully');
    } catch (err) {
      setSaveMsg('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)', fontSize: 13 }}>Loading...</div>;
  }

  const vectorScores = {};
  GES_VECTORS.forEach((v) => {
    vectorScores[v.id] = calculateVectorScore(v.parameters, paramState[v.id]);
  });
  const overall = calculateOverallGES(vectorScores);

  return (
    <div>
      <div style={{
        padding: '12px 16px',
        background: '#fff8e1',
        border: '1px solid #c9952a',
        borderRadius: 8,
        marginBottom: 20,
        fontSize: 13,
        color: '#7a5c0a',
        lineHeight: 1.6,
      }}>
        This assessment is completed during Due Diligence after IC1 approval. Complete it now
        if DD has been approved.
      </div>

      {overall != null && (
        <div style={{
          padding: '12px 16px',
          background: scoreBg(overall),
          border: `1px solid ${scoreColor(overall)}`,
          borderRadius: 8,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: 2 }}>
              Overall GES Score
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: scoreColor(overall) }}>
              {overall} / 5
              <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 8 }}>
                ({Math.round(overall * 2 * 10) / 10} / 10)
              </span>
            </div>
          </div>
        </div>
      )}

      {GES_VECTORS.map((vector) => {
        const vs = vectorScores[vector.id];
        return (
          <div key={vector.id} className="card mb-4">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{vector.name}</h3>
              {vs != null && (
                <span style={{
                  padding: '3px 10px',
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 700,
                  background: scoreBg(vs),
                  color: scoreColor(vs),
                  border: `1px solid ${scoreColor(vs)}`,
                }}>
                  {vs} / 5
                </span>
              )}
            </div>
            <div className="card-body">
              {vector.parameters.map((param) => {
                const ps = paramState[vector.id][param.id];
                return (
                  <div key={param.id} style={{
                    marginBottom: 16,
                    paddingBottom: 16,
                    borderBottom: '1px solid var(--color-border-light)',
                    opacity: ps.na ? 0.5 : 1,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, flex: 1, marginRight: 12 }}>
                        {param.label}
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={ps.na}
                          onChange={() => toggleNa(vector.id, param.id)}
                        />
                        N/A
                      </label>
                    </div>

                    {!ps.na && param.type === 'management_framework' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {PRACTICES.map((practice) => (
                          <label key={practice} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={ps.practices_present.includes(practice)}
                              onChange={() => togglePractice(vector.id, param.id, practice)}
                            />
                            {practice}
                          </label>
                        ))}
                        {ps.score != null && (
                          <span style={{
                            marginLeft: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            color: scoreColor(ps.score),
                          }}>
                            Score: {ps.score} / 5
                          </span>
                        )}
                      </div>
                    )}

                    {!ps.na && param.type === 'quantitative_band' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <input
                          type="number"
                          className="form-input"
                          value={ps.input_pct}
                          onChange={(e) => setInputPct(vector.id, param.id, e.target.value, param)}
                          placeholder="Enter %"
                          min={0}
                          max={999}
                          style={{ width: 120 }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>%</span>
                        {ps.score != null && (
                          <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor(ps.score) }}>
                            → Score: {ps.score} / 5
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: '1px solid var(--color-accent)',
            background: 'var(--color-accent)',
            color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save GES Assessment'}
        </button>
        {saveMsg && (
          <span style={{
            fontSize: 13,
            color: saveMsg.includes('failed') ? 'var(--color-error)' : 'var(--color-success)',
          }}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
}
